import React, { type ComponentProps, forwardRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { HostedFrame, type HostedFrameRef, theme } from '@sg/garnish';

import { getEnvVars } from '@order/utils';

import { useIsApplePaySupported } from '../hooks';
import { FRAME_ID } from '../utils';

export const ApplePayHostedFrame = forwardRef<
  HostedFrameRef,
  ApplePayHostedFrameProps
>((props, ref) => {
  const { onMessage } = props;

  const { formatMessage } = useIntl();

  if (!useIsApplePaySupported()) return null;

  return (
    <HostedFrame
      ref={ref}
      frameId={FRAME_ID}
      frameHeight={0}
      title={formatMessage(messages.applePayFrameTitle)}
      source={applePaySource}
      style={styles.content}
      containerStyle={styles.content}
      onMessage={onMessage}
    />
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  applePayFrameTitle: {
    defaultMessage: 'Apple Pay',
    description: 'Apple Pay | Hosted Frame Title',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ApplePayHostedFrameProps = Pick<
  ComponentProps<typeof HostedFrame>,
  'ref' | 'onMessage'
>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flex: 0,
    height: 0,
    backgroundColor: theme.colors.APP_BACKGROUND,
    marginVertical: theme.spacing['1'],
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const applePaySource = {
  uri: getEnvVars().STRIPE_EMBEDDED_APPLE_PAY_PAYMENT_SHEET_URL,
};
